// frontend/src/features/ups/modals/SiteDrawer.js
// UPS Admin: Site Drawer
// - Read-only code (immutable)
// - Rename site (name only)
// - Deactivate / Reactivate via PATCH /ups-admin/sites/:id { status: 'inactive'|'active' }

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

function normalizeStatus(s) {
  const v = (s ?? '').toString().trim().toLowerCase();
  if (v === 'inactive') return 'inactive';
  return 'active';
}

function StatusBadge({ status }) {
  const st = normalizeStatus(status);
  const cls =
    st === 'active'
      ? 'bg-green-50 text-green-700 border-green-200'
      : 'bg-gray-50 text-gray-700 border-gray-200';

  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded border ${cls}`}>
      {st}
    </span>
  );
}

export default function SiteDrawer({ isOpen, site, onClose, onChanged }) {
  const [tab, setTab] = useState('details'); // details | agencies

  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [error, setError] = useState('');

  // Local copy to reflect status changes immediately without relying on parent refresh timing
  const [localSite, setLocalSite] = useState(null);

  const status = useMemo(() => normalizeStatus(localSite?.status), [localSite]);

  useEffect(() => {
    if (!isOpen || !site) return;

    setTab('details');
    setError('');
    setSaving(false);
    setStatusBusy(false);

    setLocalSite(site);
    setName(site?.name || '');
  }, [isOpen, site]);

  const saveName = async () => {
    if (!localSite?.id) return;
    setError('');

    const n = (name ?? '').trim();
    if (!n) return setError('Site name is required');

    setSaving(true);
    try {
      const res = await axios.patch(`/ups-admin/sites/${localSite.id}`, { name: n });

      // Support { site } or direct site response; otherwise just patch locally
      const updated = res.data?.site ?? res.data ?? null;
      setLocalSite((prev) => ({
        ...(prev || {}),
        ...(updated && typeof updated === 'object' ? updated : {}),
        name: n,
      }));

      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update site');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (nextStatus) => {
    if (!localSite?.id) return;
    const next = normalizeStatus(nextStatus);
    setError('');

    setStatusBusy(true);
    try {
      const res = await axios.patch(`/ups-admin/sites/${localSite.id}`, { status: next });

      const updated = res.data?.site ?? res.data ?? null;
      setLocalSite((prev) => ({
        ...(prev || {}),
        ...(updated && typeof updated === 'object' ? updated : {}),
        status: next,
      }));

      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update status');
    } finally {
      setStatusBusy(false);
    }
  };

  if (!isOpen || !site) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose} />

      {/* drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl border-l">
        {/* header */}
        <div className="p-5 border-b">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs text-gray-500">Site</div>
              <div className="flex items-center gap-2">
                <div className="text-xl font-semibold text-gray-800">{localSite?.name || site.name}</div>
                <StatusBadge status={localSite?.status} />
              </div>
              <div className="text-sm text-gray-500">
                Code: <span className="font-mono text-xs">{localSite?.code || site.code || '—'}</span>
              </div>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              ✕
            </button>
          </div>

          {/* tabs */}
          <div className="mt-4 flex gap-2">
            <button
              className={`px-3 py-2 rounded text-sm border ${
                tab === 'details'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setTab('details')}
            >
              Details
            </button>
            <button
              className={`px-3 py-2 rounded text-sm border ${
                tab === 'agencies'
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              onClick={() => setTab('agencies')}
            >
              Agencies
            </button>
          </div>
        </div>

        {/* body */}
        <div className="p-5 overflow-y-auto h-[calc(100%-104px)]">
          {error && (
            <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded p-2">
              {error}
            </div>
          )}

          {tab === 'details' && (
            <div className="space-y-5">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Site name</label>
                <input
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={saving || statusBusy}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-600 mb-1">Code (immutable)</label>
                <input
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none font-mono bg-gray-50 text-gray-700"
                  value={(localSite?.code || site.code || '').toString()}
                  readOnly
                />
                <div className="text-xs text-gray-500 mt-1">
                  Code can’t be changed after creation.
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <div className="text-sm text-gray-600">
                  Status: <StatusBadge status={localSite?.status} />
                </div>

                <div className="flex items-center gap-2">
                  {status === 'active' ? (
                    <button
                      onClick={() => setStatus('inactive')}
                      disabled={statusBusy || saving}
                      className="px-4 py-2 rounded border text-gray-700 hover:bg-gray-50 transition text-sm disabled:opacity-60"
                    >
                      {statusBusy ? 'Updating…' : 'Deactivate'}
                    </button>
                  ) : (
                    <button
                      onClick={() => setStatus('active')}
                      disabled={statusBusy || saving}
                      className="px-4 py-2 rounded border text-gray-700 hover:bg-gray-50 transition text-sm disabled:opacity-60"
                    >
                      {statusBusy ? 'Updating…' : 'Reactivate'}
                    </button>
                  )}

                  <button
                    onClick={saveName}
                    disabled={saving || statusBusy}
                    className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 transition text-sm disabled:opacity-60"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>

              <div className="text-xs text-gray-500">
                Created:{' '}
                {localSite?.created_at || site.created_at
                  ? new Date(localSite?.created_at || site.created_at).toLocaleString()
                  : '—'}
              </div>
            </div>
          )}

          {tab === 'agencies' && (
            <div className="text-sm text-gray-600">
              <div className="mb-2 font-semibold text-gray-800">Linked agencies</div>
              <div className="text-gray-500">
                Next step: list agencies + contract status + per-agency “Configured allowed at this site”
                toggle backed by your contract_sites endpoint.
              </div>
              <div className="mt-3 text-xs text-gray-500">
                This drawer already supports the control-plane actions (rename, deactivate/reactivate).
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
